import { captureMessage, sendFeedback, withScope } from "@sentry/react";
import { useState } from "react";

import { InputsContainer } from "@/components/layout/Feedback/feedback.styled-components";
import { InputRadio } from "@/components/ui/input/InputRadio";
import { InputRow } from "@/components/ui/input/InputRow";
import { InputRowInline } from "@/components/ui/input/InputRowInline";
import { InputText } from "@/components/ui/input/InputText";
import { InputTextArea } from "@/components/ui/input/InputTextArea";
import { Button } from "@/components/ui-deprecated/Button";
import { Dialog } from "@/components/ui-deprecated/Dialog";
import { useAppSelector } from "@/store";
import { splitAndCapitalize } from "@/utils/string.utils";

const feedbackType: Record<FeedbackType, string> = {
    issue: "Issue",
    question: "Question",
    idea: "Idea",
    compliment: "Compliment",
};

type FeedbackType = "issue" | "question" | "idea" | "compliment";
type Props = {
    onClose: () => void;
    type?: FeedbackType;
};

const FeedbackModal = (props: Props) => {
    const { onClose } = props;

    const [type, setType] = useState<FeedbackType | null>(props.type ?? null);
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("");

    const userName = useAppSelector((state) => state.user.name);

    const handleSendFeedback = () => {
        withScope(function (scope) {
            // Generate a random issue id to be able to track the feedback
            const issueId = Math.random().toString(16).substring(2, 10).toUpperCase();

            // Add tags to the scope to be able to filter the feedback
            // The tags have to be set before calling `Sentry.captureMessage`
            scope.setTag("feedback.id", issueId);
            scope.setTag("feedback.type", type);

            if (email) {
                const user = scope.getUser();
                scope.setUser({ ...user, email: email });
            }

            const maxTitleLength = 60; // Without the type prefix
            let messageTrimmed = message.replace(/\n/g, " ").substring(0, maxTitleLength).trim();
            // Add ellipsis if the original message was longer than 20 characters
            messageTrimmed = messageTrimmed.length < message.length ? `${messageTrimmed}...` : messageTrimmed;
            // @ts-expect-error FIXME: type cannot be null here
            const messageTitle = `${splitAndCapitalize(type)} - ${messageTrimmed}`;
            const eventId = captureMessage(messageTitle);

            const userFeedback = {
                event_id: eventId,
                name: userName,
                email: email,
                message,
            };

            sendFeedback(userFeedback, { includeReplay: true });
        });

        setType("compliment");
        setMessage("");
        setEmail("");
        onClose();
    };

    return (
        <Dialog.Content
            width={"sm"}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
        >
            <Dialog.Content.Title>Feedback</Dialog.Content.Title>
            <Dialog.Content.Body>
                <InputsContainer>
                    <div>
                        {Object.entries(feedbackType).map(([key, label]) => (
                            <InputRowInline key={key}>
                                <InputRadio
                                    type="radio"
                                    id={`type-${key}`}
                                    name="type"
                                    value={key}
                                    checked={type === key}
                                    onChange={() => setType(key as FeedbackType)}
                                />
                                <label htmlFor={`type-${key}`}>{label}</label>
                            </InputRowInline>
                        ))}
                    </div>
                    {type && (
                        <>
                            <InputRow>
                                <label htmlFor="message" style={{ display: "block" }}>
                                    Your {feedbackType[type]}:
                                </label>
                                <InputTextArea
                                    id={"message"}
                                    name={"message"}
                                    value={message}
                                    required={true}
                                    onChange={(event) => setMessage(event.target.value)}
                                    placeholder={"Please describe your feedback in detail"}
                                    rows={4}
                                />
                            </InputRow>
                            <InputRow>
                                <label htmlFor="email">
                                    Please enter your email if you want us to get back to you:
                                </label>
                                <InputText
                                    type={"email"}
                                    id={"email"}
                                    name={"email"}
                                    value={email}
                                    required={false}
                                    onChange={(event) => setEmail(event.target.value)}
                                    placeholder={"Please enter your email address"}
                                />
                            </InputRow>
                        </>
                    )}
                </InputsContainer>
            </Dialog.Content.Body>
            <Dialog.Content.Actions>
                <Button variant={"text"} onClick={onClose}>
                    Close
                </Button>
                <Button onClick={handleSendFeedback} disabled={message === ""}>
                    Send
                </Button>
            </Dialog.Content.Actions>
        </Dialog.Content>
    );
};

export { FeedbackModal };
